import { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { graphql, createFragmentContainer } from 'react-relay/legacy';
import { useSbSelector } from '../../reducers/useSbSelector';

import { FormattedMessage } from 'dibs-react-intl';
import { ExpandingArea } from 'dibs-elements/exports/ExpandingArea';
import { SearchTag } from 'dibs-elements/exports/SearchTag';

import { useMeasurementUnit } from '../useMeasurementUnit';
import { useRingMeasurementType } from '../hooks/ringSize/useRingMeasurementType';
import { getFilterValues } from '../SbSharedRefineMenu/sbSharedRefineMenuHelpers';
import {
    getAppliedFilterValues,
    closeAppliedFilterHandler,
    clearAllAppliedFiltersHandler,
    getValidSharedAppliedFilters,
} from '../SbSharedAppliedFilters/sbSharedAppliedFiltersHelpers';
import { isBatchAppliedFilter } from '../SbRespRefineMenu/SbRespRefineMenuFilters/sbRespRefineMenuFiltersMap';
import { SbRespAppliedFiltersClearAll } from './SbRespAppliedFiltersClearAll';
import { SbRespAppliedFiltersItem as SbSharedAppliedFiltersItem } from './SbRespAppliedFiltersItem';

import dibsCss from 'dibs-css';
import classnames from 'classnames';
import styles from './SbRespAppliedFilters.scss';

const FILTER_THRESHOLD = 5;

export const SbRespAppliedFiltersComponent = ({ itemSearch, currency, user }) => {
    const dispatch = useDispatch();
    const generatedUriRef = useSbSelector(state => state.filters.generatedUriRef);
    const { appliedFilters, filters } = itemSearch;
    const [isViewingMoreFilters, setIsViewingMoreFilters] = useState(false);

    const validAppliedFilters = getValidSharedAppliedFilters(appliedFilters);

    const [measurementUnit] = useMeasurementUnit({ user, itemSearch });
    const [ringMeasurementType] = useRingMeasurementType({ currency, itemSearch });

    const onClearAllClick = clearAllAppliedFiltersHandler(dispatch, itemSearch);
    const onCloseClick = closeAppliedFilterHandler(dispatch);

    if (!validAppliedFilters.length) {
        return null;
    }

    const tags = validAppliedFilters.reduce((acc, appliedFilter) => {
        const isBatchApplied = isBatchAppliedFilter(appliedFilter.name);
        const values = getAppliedFilterValues({
            appliedFilter,
            isBatchApplied,
        });

        values.forEach((filterValue, index) => {
            acc.push(
                <SbSharedAppliedFiltersItem
                    key={`${appliedFilter.name}-${index}`}
                    currency={currency}
                    filterName={appliedFilter.name}
                    localizedFilterName={appliedFilter.localizedFilterName}
                    filterValue={isBatchApplied ? null : filterValue}
                    availableValues={getFilterValues(filters, appliedFilter.name)}
                    onCloseClick={event =>
                        onCloseClick({
                            filterName: appliedFilter.name,
                            filterValue,
                            generatedUriRef,
                            isBatchApplied,
                            event,
                        })
                    }
                    measurementUnit={measurementUnit}
                    ringMeasurementType={ringMeasurementType}
                />
            );
        });
        return acc;
    }, []);
    const numberMoreFilters = Math.max(tags.length - FILTER_THRESHOLD, 0);
    if (numberMoreFilters === 0 && isViewingMoreFilters) {
        setIsViewingMoreFilters(false);
    }
    const extraTags = tags.splice(FILTER_THRESHOLD);

    return (
        <div className={classnames(dibsCss.flex, dibsCss.flexWrap)}>
            <div
                className={classnames(dibsCss.flex, dibsCss.flexWrap, dibsCss.itemsBaseline)}
                data-tn="applied-filters-container"
            >
                {tags}
                {!!numberMoreFilters && (
                    <>
                        {isViewingMoreFilters && (
                            <ExpandingArea
                                expanded={isViewingMoreFilters}
                                childrenWrapperClass={classnames(dibsCss.flex, dibsCss.flexWrap)}
                            >
                                {extraTags}
                            </ExpandingArea>
                        )}
                        <div
                            onClick={() => setIsViewingMoreFilters(!isViewingMoreFilters)}
                            onKeyDown={() => setIsViewingMoreFilters(!isViewingMoreFilters)}
                            className={classnames(
                                dibsCss.flex,
                                dibsCss.cursorPointer,
                                dibsCss.prXsmall,
                                dibsCss.pbXsmall
                            )}
                            role="button"
                            tabIndex="0"
                        >
                            <SearchTag hasDeleteButton={false} dataTn={'more-filters-button'}>
                                <div
                                    className={classnames(
                                        styles.moreFiltersButton,
                                        dibsCss.sassyFontBodySizeNoneTypeHeavy
                                    )}
                                >
                                    {isViewingMoreFilters ? (
                                        <FormattedMessage
                                            id="sb.SbRespAppliedFilters.viewLess"
                                            defaultMessage="View Less"
                                        />
                                    ) : (
                                        <FormattedMessage
                                            id="sb.SbRespAppliedFilters.additionalFilters"
                                            defaultMessage="+ {numberMoreFilters} more"
                                            values={{ numberMoreFilters }}
                                        />
                                    )}
                                </div>
                            </SearchTag>
                        </div>
                    </>
                )}
                <SbRespAppliedFiltersClearAll onClearAllClick={onClearAllClick} />
            </div>
        </div>
    );
};

SbRespAppliedFiltersComponent.propTypes = {
    user: PropTypes.object,
    currency: PropTypes.string.isRequired,
    itemSearch: PropTypes.object.isRequired,
};

export const SbRespAppliedFilters = createFragmentContainer(SbRespAppliedFiltersComponent, {
    itemSearch: graphql`
        fragment SbRespAppliedFilters_itemSearch on ItemSearchQueryConnection {
            ...useMeasurementUnit_itemSearch
            ...useRingMeasurementType_itemSearch
            ...sbSharedAppliedFiltersHelpers_itemSearch
            filters {
                name
                localizedFilterName
                values {
                    displayName
                    urlLabel
                    linkReference
                    properties {
                        mm
                        us
                    }
                }
            }
            appliedFilters {
                name
                localizedFilterName
                canBeDismissed
                values {
                    urlLabel
                    linkReference
                    displayName
                    hexCode
                    properties {
                        mm
                        us
                    }
                }
            }
        }
    `,
    user: graphql`
        fragment SbRespAppliedFilters_user on User {
            ...useMeasurementUnit_user
        }
    `,
});
